/**
 * Byte-pattern encoding detection for CSV buffers.
 *
 * Exchange exports are overwhelmingly UTF-8, and UTF-16 ones usually carry a
 * BOM. But some (Wirex statement exports) emit UTF-16LE with NO BOM, which a
 * naive UTF-8 decode turns into NUL-interleaved garbage ("C\0o\0m\0p\0…") that
 * makes every column header unreadable — and a UTF-8 fatal-decode does NOT
 * catch it because NUL (0x00) is a valid UTF-8 code point.
 *
 * This detector is BOM-independent: ASCII-dominant UTF-16 text has a NUL in
 * every other byte (odd positions for little-endian, even positions for
 * big-endian), whereas real UTF-8 text has virtually no NUL bytes at all. So a
 * strong, parity-aligned NUL pattern is an unambiguous UTF-16 signal. Pure +
 * isomorphic (Uint8Array) so the frontend (ArrayBuffer) and backend (Buffer)
 * share one implementation.
 */

export type Utf16Encoding = 'utf-16le' | 'utf-16be'

/**
 * Detect UTF-16 (LE or BE) in a buffer that has NO BOM. Returns the encoding
 * when the parity-aligned NUL pattern is strong enough, otherwise `null` (let
 * the caller fall back to its default, normally UTF-8). Callers should check
 * for an explicit BOM FIRST — this only handles the no-BOM case.
 */
export function detectUtf16WithoutBom(bytes: Uint8Array): Utf16Encoding | null {
  // Sample an even-length prefix (UTF-16 code units are 2 bytes). 4 KiB is
  // plenty to classify and bounds the work on large files.
  const sample = Math.min(bytes.length - (bytes.length % 2), 4096)
  if (sample < 16) return null // too short to classify confidently

  let nulAtEven = 0 // byte[0], byte[2], … → high byte on big-endian
  let nulAtOdd = 0 // byte[1], byte[3], … → high byte on little-endian
  for (let i = 0; i < sample; i += 2) {
    if (bytes[i] === 0x00) nulAtEven++
    if (bytes[i + 1] === 0x00) nulAtOdd++
  }

  const pairs = sample / 2
  // Require the NUL on the great majority of code units on ONE parity and
  // almost none on the other — ASCII-dominant UTF-16. UTF-8 text has ~0 NULs,
  // so it never reaches these thresholds.
  if (nulAtOdd >= pairs * 0.8 && nulAtEven <= pairs * 0.1) return 'utf-16le'
  if (nulAtEven >= pairs * 0.8 && nulAtOdd <= pairs * 0.1) return 'utf-16be'
  return null
}
